<template>
  <ListViewBanner :add-route="{ name: 'app_framework_new' }">
    Frameworks
  </ListViewBanner>

  <FrameworkSearch @search="searchParams = $event" />

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th class="w-16">
          Icon
        </th>
        <th>Full Name</th>
        <th>Short Name</th>
        <th class="text-right">
          Sort Order
        </th>
        <th class="text-right">
          No. of Categories
        </th>
        <th>
          <span class="sr-only">Actions</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!frameworks?.length"
        :colspan="6"
      >
        <template v-if="frameworks?.length">
          <tr
            v-for="framework in frameworks"
            :key="framework.id"
            class="overflow-visible"
          >
            <td class="flex items-center gap-2">
              <img
                class="block h-10 w-auto"
                :alt="framework.name"
                :src="framework.icon"
              >
            </td>

            <td>
              <RouterLink
                class="text-secondary-alt"
                :to="{ name: `app_category_list`, params: { framework: framework.id } }"
              >
                {{ framework.name }}
              </RouterLink>

              <template v-if="framework.archived">
                <BadgeArchived />
              </template>
            </td>

            <td>
              {{ framework.shortName }}
            </td>

            <td class="text-right">
              {{ framework?.sortOrder }}
            </td>

            <td class="text-right">
              {{ framework.categories.length }}
            </td>

            <td>
              <template v-if="framework.archived">
                <RestoreButton @click="entityToRestore = framework" />
              </template>

              <template v-else>
                <AppDropdown>
                  <AppDropdownItem
                    text="Edit"
                    :to="{ name: 'app_framework_edit', params: { framework: framework.id }}"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="entityToArchive = framework"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="entityToArchive">
    <ConfirmationModal
      title="Archive Framework"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="entityToArchive = false"
    >
      <p>
        <span class="block">Name: {{ entityToArchive?.name }}</span>
        Are you sure you want to <strong>ARCHIVE</strong> this Framework?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="entityToRestore">
    <ConfirmationModal
      title="Restore Framework"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="entityToRestore = false"
    >
      <p>
        <span class="block">Name: {{ entityToRestore?.name }}</span>
        Are you sure you want to <strong>RESTORE</strong> this Framework?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { ref, watchEffect } from 'vue';
import { useEntityArchiver, useEntityRestorer, useHttp } from '@/composables';
import { HttpStatusCode } from 'axios';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { FrameworkSearch } from '@/components/forms/search';
import { ConfirmationModal } from '@/components/modals';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { RestoreButton } from '@/components/buttons/index.js';

const loading = ref(true);
const frameworks = ref([]);
const searchParams = ref({ page: 1 });
const paginationData = ref({ page: 1, itemsPerPage: 10 });

const loadFrameworks = async () => {
  loading.value = true;
  frameworks.value = [];

  const response = await useHttp().get('/frameworks', searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    frameworks.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const { archiving, entityToArchive, archiveEntity } = useEntityArchiver('frameworks', loadFrameworks);
const { restoring, entityToRestore, restoreEntity } = useEntityRestorer('frameworks', loadFrameworks);

watchEffect(loadFrameworks);
</script>
